import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardContent, Typography, TextField, Button, Grid, Box, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, CardMedia, Chip,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
  Alert, Stack, Divider, Paper, Tooltip, Fade, Collapse, InputAdornment,
  List, ListItem, ListItemText, ListItemSecondaryAction, FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Translate as TranslateIcon,
  Category as CategoryIcon,
  CloudUpload as CloudUploadIcon,
  LocalShipping as DeliveryIcon,
  Store as StoreIcon,
  Map as MapIcon,
  DeleteSweep as DeleteSweepIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';
import { productApi, categoryApi, imageApi, providerApi, locationApi } from '../../services/api';
import { formatProductPrice, getCurrencySymbol } from '../../utils/currencyUtils';
import DeliverySettings from './DeliverySettings';

// --- GOOGLE MAPS CONFIG ---
// Runtime-injected key (env.js) takes precedence over the build-time key
const GOOGLE_MAPS_API_KEY =
  (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__.GOOGLE_MAPS_API_KEY) ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY';
const MAP_LIBRARIES = ['drawing', 'geometry'];
const mapStyle = { width: '100%', height: '400px', borderRadius: '8px', marginTop: '16px' };
const defaultCenter = { lat: 32.0853, lng: 34.7818 };

// --- HELPER: TabPanel ---
const TabPanel = ({ children, value, index, ...other }) => (
    <div role="tabpanel" hidden={value !== index} {...other} style={{ height: '100%' }}>
      {value === index && <Box sx={{ pt: 3, height: '100%' }}>{children}</Box>}
    </div>
);

// --- COMPONENT: Delivery Zone Management (User Provided Code) ---
const DeliveryZoneManagement = ({ providerData }) => {
  const { t } = useTranslation();
  const [deliveryZone, setDeliveryZone] = useState([]);
  const [tempPoints, setTempPoints] = useState([]);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES
  });

  const parsePoints = (data) => {
    if (!Array.isArray(data)) return [];
    return data
        .map(p => ({
          lat: Number(p.latitude || p.lat),
          lng: Number(p.longitude || p.lng)
        }))
        .filter(p => !isNaN(p.lat) && !isNaN(p.lng));
  };

  useEffect(() => {
    if (providerData?.id) {
      providerApi.getDeliveryZone(providerData.id)
          .then(data => setDeliveryZone(parsePoints(data)))
          .catch(console.error);
    }
  }, [providerData?.id]);

  const saveDeliveryZone = async () => {
    if (tempPoints.length > 0 && tempPoints.length < 3) {
      alert(t('delivery.minPoints'));
      return;
    }
    setLoading(true);
    try {
      const payload = tempPoints.map((p, i) => ({
        latitude: p.lat,
        longitude: p.lng,
        sequence_order: i
      }));
      await providerApi.updateDeliveryZone(providerData.id, payload);
      setDeliveryZone(tempPoints);
      setMapDialogOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onMapLoad = (map, points) => {
    if (points && points.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(p => bounds.extend(p));
      map.fitBounds(bounds);
    }
  };

  if (loadError) return <Alert severity="error">{t('delivery.mapLoadFailed')}</Alert>;

  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_API_KEY') {
    return <Alert severity="warning" sx={{ mb: 2 }}>{t('delivery.apiKeyMissing')} <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> / <code>window.__ENV__.GOOGLE_MAPS_API_KEY</code></Alert>;
  }

  return (
      <Box sx={{ width: '100%', mb: 3 }}>
        <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <MapIcon sx={{ mr: 1, color: '#667eea' }} /> {t('delivery.title')}
              </Typography>
              <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setTempPoints([...deliveryZone]);
                    setMapDialogOpen(true);
                  }}
                  sx={{ borderRadius: 2 }}
              >
                {deliveryZone.length > 0 ? t('delivery.editZone') : t('delivery.setZone')}
              </Button>
            </Box>

            {isLoaded ? (
                <GoogleMap
                    mapContainerStyle={mapStyle}
                    center={deliveryZone.length > 0 ? deliveryZone[0] : defaultCenter}
                    zoom={12}
                    onLoad={(map) => onMapLoad(map, deliveryZone)}
                >
                  {deliveryZone.length >= 3 && (
                      <Polygon
                          paths={deliveryZone}
                          options={{
                            fillColor: "#4CAF50",
                            fillOpacity: 0.35,
                            strokeColor: "#2E7D32",
                            strokeWeight: 2,
                          }}
                      />
                  )}
                  {deliveryZone.map((p, i) => (
                      <Marker key={i} position={p} />
                  ))}
                </GoogleMap>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            )}
          </CardContent>
        </Card>

        {/* EDIT MODAL */}
        <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{t('delivery.dialogTitle')}</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {isLoaded && (
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '500px' }}
                    center={tempPoints.length > 0 ? tempPoints[0] : defaultCenter}
                    zoom={12}
                    onClick={(e) => setTempPoints([...tempPoints, { lat: e.latLng.lat(), lng: e.latLng.lng() }])}
                    onLoad={(map) => onMapLoad(map, tempPoints)}
                >
                  {tempPoints.length >= 3 && (
                      <Polygon paths={tempPoints} />
                  )}
                  {tempPoints.map((p, i) => (
                      <Marker
                          key={i}
                          position={p}
                          label={`${i+1}`}
                          onClick={() => setTempPoints(tempPoints.filter((_, idx) => idx !== i))}
                      />
                  ))}
                </GoogleMap>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setTempPoints([])}
            >
              {t('delivery.clearAll')}
            </Button>
            <Box>
              <Button onClick={() => setMapDialogOpen(false)} sx={{ mr: 1 }}>{t('common.cancel')}</Button>
              <Button variant="contained" onClick={saveDeliveryZone} disabled={loading}>
                {t('delivery.saveButton')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

// Responsive product grid: equal-width columns, up to 5 per row on wide screens.
// Uses CSS grid (not MUI <Grid item>) because MUI v7 ignores the legacy item/xs props.
const PRODUCT_GRID_SX = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',
    sm: 'repeat(3, 1fr)',
    md: 'repeat(4, 1fr)',
    lg: 'repeat(5, 1fr)',
  },
};

// --- COMPONENT: Product Card ---
const ProductCard = React.memo(({ product, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const imageUrl = product.imageUrls && product.imageUrls.length > 0 ? product.imageUrls[0] : null;

  return (
      <Fade in timeout={300}>
        <Card
            sx={{
              height: '100%', display: 'flex', flexDirection: 'column',
              borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none',
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 18px rgba(0,0,0,0.08)' }
            }}
        >
          <Box sx={{ position: 'relative', paddingTop: '70%', backgroundColor: '#f5f5f7' }}>
            {imageUrl ? (
                <CardMedia
                    component="img"
                    image={imageUrl}
                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ImageIcon sx={{ fontSize: 40, color: '#e0e0e0' }} />
                </Box>
            )}
            {!product.active && (
                <Chip label={t('store.inactive')} size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)' }} />
            )}
            {product.featured && (
                <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '50%', p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StarIcon color="primary" fontSize="small" />
                </Box>
            )}
          </Box>
          <CardContent sx={{ flexGrow: 1, p: 1.5, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="start" spacing={0.75}>
              <Typography variant="body2" fontWeight={700} noWrap title={product.name} sx={{ minWidth: 0 }}>
                {product.name}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary" noWrap sx={{ flexShrink: 0 }}>
                {formatProductPrice(product)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 1, height: 32, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {product.description || t('services.noDescription')}
            </Typography>
            {/* Fixed-height chip row keeps every card identical regardless of chip wrapping */}
            <Box sx={{ mt: 'auto', height: 26, display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden', flexWrap: 'nowrap' }}>
              <Chip
                  label={product.stockQuantity > 0 ? t('store.inStock', { count: product.stockQuantity }) : t('store.outOfStock')}
                  size="small"
                  color={product.stockQuantity > 0 ? "success" : "error"}
                  variant="outlined"
                  sx={{ flexShrink: 0, height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.68rem' } }}
              />
              {product.sku && <Chip label={product.sku} size="small" variant="outlined" sx={{ flexShrink: 1, minWidth: 0, height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis' } }} />}
            </Box>
          </CardContent>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', gap: 0.75 }}>
            <Button fullWidth variant="outlined" size="small" startIcon={<EditIcon />} onClick={onEdit}>{t('common.edit')}</Button>
            <IconButton size="small" color="error" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        </Card>
      </Fade>
  );
});

// --- COMPONENT: Products Tab (Contains Translation Logic) ---
const ProductsTabContent = ({ providerData }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('$');

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCategoryForNew, setSelectedCategoryForNew] = useState(null);

  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const importFileInputRef = React.useRef(null);

  // Form State
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productPricePer100g, setProductPricePer100g] = useState('');
  const [productSku, setProductSku] = useState('');
  const [productStockQuantity, setProductStockQuantity] = useState('');
  const [productCategoryId, setProductCategoryId] = useState('');
  const [productImageUrls, setProductImageUrls] = useState([]);
  const [productFeatured, setProductFeatured] = useState(false);

  // --- TRANSLATION STATE & CONFIG ---
  const [productTranslations, setProductTranslations] = useState({});
  const [selectedLocale, setSelectedLocale] = useState('');
  const [translationName, setTranslationName] = useState('');
  const [translationDescription, setTranslationDescription] = useState('');

  const supportedLocales = [
    { code: 'en', name: t('store.langEn') }, { code: 'es', name: t('store.langEs') },
    { code: 'fr', name: t('store.langFr') }, { code: 'he', name: t('store.langHe') }, { code: 'ar', name: t('store.langAr') },
  ];

  useEffect(() => { 
    loadProducts(); 
    loadCategories(); 
    loadCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCurrency = async () => {
    try {
      // Get currency based on provider's user country
      if (providerData?.user?.country) {
        const data = await locationApi.getCurrencyByCountry(providerData.user.country?.nameEn ?? providerData.user.country);
        if (data.currencyCode) {
          setCurrencySymbol(getCurrencySymbol(data.currencyCode));
        } else if (data.currencySymbol) {
          setCurrencySymbol(data.currencySymbol);
        }
      }
    } catch (error) {
      console.error('Failed to load currency:', error);
      // Keep default $ symbol
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const result = await productApi.getProductsByProvider(providerData.id);
      const raw = result.content || result;
      // Map Snake Case -> Camel Case and parse translations
      setProducts(raw.map(p => ({
        ...p,
        categoryId: p.category_id,
        categoryName: p.category_name,
        stockQuantity: p.stock_quantity,
        imageUrls: p.image_urls || [],
        featured: p.featured || false,
        translations: p.translations || {}, // Ensure translations exist as Map<locale, {name, description}>
        inStock: p.in_stock
      })));
    } catch (err) { 
      console.error('Error loading products:', err);
      setError(t('store.loadFailed') + ': ' + (err.response?.data?.message || err.message)); 
    } finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try { setCategories(await categoryApi.getRootCategories()); } catch (e) { console.error(e); }
  };

  const resetForm = useCallback(() => {
    setProductName(''); setProductDescription(''); setProductPrice('');
    setProductPricePer100g('');
    setProductSku(''); setProductStockQuantity(''); setProductCategoryId('');
    setProductImageUrls([]); setProductTranslations({}); setProductFeatured(false);
    setSelectedLocale(''); setTranslationName(''); setTranslationDescription('');
    setError(''); // Clear any errors when resetting form
    setCurrentTab(0); // Reset to first tab
  }, []);

  const populateForm = useCallback((product) => {
    setProductName(product.name || '');
    setProductDescription(product.description || '');
    setProductPrice(product.price || '');
    setProductPricePer100g(product.price_per_100g ?? '');
    setProductSku(product.sku || '');
    setProductStockQuantity(product.stockQuantity || '');
    setProductCategoryId(product.categoryId || '');
    setProductImageUrls(product.imageUrls || []);
    setProductFeatured(product.featured || false);

    // Handle translations - ensure they're in the correct format
    const translations = product.translations || {};
    console.log('Loading product translations:', translations);
    setProductTranslations(translations);
  }, []);

  const handleOpenDialog = useCallback((product = null, catId = null) => {
    if (product) {
      setEditingProduct(product); populateForm(product); setSelectedCategoryForNew(null);
    } else {
      setEditingProduct(null); resetForm();
      if (catId) { setProductCategoryId(catId); setSelectedCategoryForNew(catId); }
    }
    setCurrentTab(0); setDialogOpen(true);
  }, [populateForm, resetForm]);

  const handleCloseDialog = useCallback(() => { setDialogOpen(false); resetForm(); }, [resetForm]);

  // --- Translation Handlers ---
  const handleAddTranslation = () => {
    if (!selectedLocale || !translationName.trim()) {
      setError(t('store.translationRequired'));
      return;
    }
    
    // Validate translation name length (backend expects max 200 chars)
    if (translationName.trim().length > 200) {
      setError(t('store.translationNameTooLong'));
      return;
    }
    
    // Validate description length (backend expects max 2000 chars)
    if (translationDescription && translationDescription.trim().length > 2000) {
      setError(t('store.translationDescTooLong'));
      return;
    }
    
    setProductTranslations(prev => ({
      ...prev,
      [selectedLocale]: {
        name: translationName.trim(),
        description: translationDescription?.trim() || null
      }
    }));
    
    // Reset inputs and clear any errors
    setSelectedLocale(''); 
    setTranslationName(''); 
    setTranslationDescription('');
    setError(''); // Clear validation errors
  };

  const handleRemoveTranslation = (locale) => {
    setProductTranslations(prev => {
      const copy = { ...prev };
      delete copy[locale];
      return copy;
    });
    setError(''); // Clear any errors when removing translations
  };

  const handleSaveProduct = async () => {
    try {
      setSaving(true);
      
      // Validate required fields
      if (!productName.trim()) {
        setError(t('store.nameRequired'));
        setSaving(false);
        return;
      }
      
      if (!productPrice || parseFloat(productPrice) < 0) {
        setError(t('store.priceRequired'));
        setSaving(false);
        return;
      }
      
      // Prepare translations in the correct format expected by backend
      // Backend expects Map<String, ProductTranslationDto> where ProductTranslationDto has {name, description}
      const formattedTranslations = Object.keys(productTranslations).length > 0 ? productTranslations : null;
      
      const payload = {
        name: productName.trim(),
        description: productDescription?.trim() || null,
        price: parseFloat(productPrice),
        price_per_100g: productPricePer100g !== '' && productPricePer100g != null ? (parseFloat(productPricePer100g) || null) : null,
        sku: productSku?.trim() || null,
        stock_quantity: parseInt(productStockQuantity) || 0,
        category_id: productCategoryId || null,
        image_urls: productImageUrls,
        featured: productFeatured,
        translations: formattedTranslations, // Send as Map<locale, {name, description}>
      };
      
      console.log('Saving product with payload:', payload);

      if (editingProduct) {
        await productApi.updateProduct(editingProduct.id, providerData.id, payload);
      } else {
        await productApi.createProduct(providerData.id, payload);
      }
      await loadProducts();
      handleCloseDialog();
      setError(''); // Clear any previous errors
    } catch (err) { 
      console.error('Error saving product:', err);
      const errorMessage = err.response?.data?.message || err.message || '';
      setError(t('store.saveFailed') + (errorMessage ? `: ${errorMessage}` : ''));
    } finally { setSaving(false); }
  };

  // ... (Image upload and Delete handlers kept same as before) ...
  const handleImageUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    setUploading(true);
    setError('');

    try {
      let uploaded = [];

      if (files.length === 1) {
        const res = await imageApi.uploadImage(files[0]);
        uploaded = [res];
      } else {
        const res = await imageApi.uploadMultipleImages(files);
        // Support either an array response or an object with images property
        uploaded = Array.isArray(res) ? res : (Array.isArray(res?.images) ? res.images : []);
        // If backend returned nothing recognizable, fall back to uploading one by one
        if (!uploaded.length) {
          const perFile = await Promise.all(files.map(f => imageApi.uploadImage(f)));
          uploaded = perFile;
        }
      }

      // Map uploaded items to URLs with sensible fallbacks
      const urls = await Promise.all(uploaded.map(async (item) => {
        const url = item?.largeUrl || item?.originalUrl || item?.url;
        if (url) return url;
        if (item?.name) {
          try {
            return await imageApi.getImageUrl(item.name, 'large');
          } catch (_) {
            try { return await imageApi.getImageUrl(item.name, 'original'); } catch { return null; }
          }
        }
        return null;
      }));

      const filtered = urls.filter(Boolean);
      if (!filtered.length) throw new Error('Image upload did not return usable URLs');

      setProductImageUrls(prev => [...prev, ...filtered]);
    } catch (err) {
      console.error('Image upload failed:', err);
      const msg = err?.response?.data?.message || err.message || 'Failed to upload image(s)';
      setError(msg);
    } finally {
      setUploading(false);
      // Reset the input so the same file can be selected again if needed
      if (input) input.value = '';
    }
  };
  const handleRemoveImage = (idx) => setProductImageUrls(prev => prev.filter((_, i) => i !== idx));
  const handleDeleteProduct = async (id) => {
    if(window.confirm(t('store.confirmDelete'))) {
      await productApi.deleteProduct(id, providerData.id);
      loadProducts();
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!importFileInputRef.current) return;
    importFileInputRef.current.value = '';
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch (parseErr) {
      setError(t('store.invalidJson') + ': ' + (parseErr.message || ''));
      return;
    }

    setImporting(true);
    setError('');
    try {
      const result = await productApi.importProducts(providerData.id, parsed);
      setImportResult(result);
      setImportResultOpen(true);
      await loadProducts();
    } catch (err) {
      console.error('Import failed:', err);
      setError(t('store.importFailed') + ': ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
    }
  };
  const toggleCategoryExpanded = (id) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const productsByCategory = useMemo(() => {
    return products.reduce((acc, p) => {
      const cat = p.categoryId || 'uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(p);
      return acc;
    }, {});
  }, [products]);

  const targetCategory = categories.find(c => c.id === selectedCategoryForNew);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

  return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">{t('store.inventoryTitle')}</Typography>
          <Stack direction="row" spacing={1}>
            <input
              ref={importFileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImportFileChange}
            />
            <Button
              variant="outlined"
              startIcon={importing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={() => importFileInputRef.current && importFileInputRef.current.click()}
              disabled={importing}
              sx={{ borderRadius: 2 }}
            >
              {importing ? t('store.importing') : t('store.importButton')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ borderRadius: 2 }}>
              {t('store.addProduct')}
            </Button>
          </Stack>
        </Box>

        <Stack spacing={2}>
          {/* Uncategorized */}
          {productsByCategory.uncategorized && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Box onClick={() => toggleCategoryExpanded('uncategorized')} sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', bgcolor: '#f8f9fa' }}>
                  <CategoryIcon sx={{ mr: 2, color: '#666' }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('store.uncategorized')}</Typography>
                  <Chip label={productsByCategory.uncategorized.length} size="small" sx={{ mr: 1 }} />
                  <IconButton size="small">{expandedCategories.has('uncategorized') ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>
                </Box>
                <Collapse in={expandedCategories.has('uncategorized')}>
                  <CardContent><Box sx={PRODUCT_GRID_SX}>{productsByCategory.uncategorized.map(p => <ProductCard key={p.id} product={p} onEdit={() => handleOpenDialog(p)} onDelete={() => handleDeleteProduct(p.id)} />)}</Box></CardContent>
                </Collapse>
              </Card>
          )}
          {/* Categorized */}
          {categories.map(cat => {
            const catProducts = productsByCategory[cat.id] || [];
            const isExpanded = expandedCategories.has(cat.id);
            return (
                <Card key={cat.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box onClick={() => toggleCategoryExpanded(cat.id)} sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', bgcolor: isExpanded ? 'rgba(102,126,234,0.04)' : '#fff' }}>
                    {cat.imageUrl ? <img src={cat.imageUrl} alt="" style={{ width: 32, height: 32, borderRadius: 4, marginRight: 16, objectFit: 'cover' }} /> : <CategoryIcon sx={{ mr: 2, color: '#667eea' }} />}
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>{cat.name}</Typography>
                    <Chip label={catProducts.length} size="small" sx={{ mr: 1 }} />
                    <Tooltip title={t('store.addToCategory')}><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(null, cat.id); }}><AddIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small">{isExpanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>
                  </Box>
                  <Collapse in={isExpanded}>
                    <CardContent sx={{ bgcolor: '#fafafa' }}>
                      {catProducts.length === 0 ? <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>{t('store.noProducts')}</Box> : <Box sx={PRODUCT_GRID_SX}>{catProducts.map(p => <ProductCard key={p.id} product={p} onEdit={() => handleOpenDialog(p)} onDelete={() => handleDeleteProduct(p.id)} />)}</Box>}
                    </CardContent>
                  </Collapse>
                </Card>
            )
          })}
        </Stack>

        {/* --- IMPORT RESULT DIALOG --- */}
        <Dialog open={importResultOpen} onClose={() => setImportResultOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ borderBottom: '1px solid #f0f0f0', py: 2, px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">{t('store.importComplete')}</Typography>
              <IconButton onClick={() => setImportResultOpen(false)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {importResult && (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="success.main">{importResult.created}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('store.created')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">{importResult.updated}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('store.updated')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="text.secondary">{importResult.skipped}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('store.skipped')}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="h4" fontWeight="bold" color="info.main">{importResult.categories_created}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('store.categories')}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1 }}>
                      {t('store.errorsCount', { count: importResult.errors.length })}
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', borderRadius: 2 }}>
                      <List dense disablePadding>
                        {importResult.errors.map((err, i) => (
                          <React.Fragment key={i}>
                            <ListItem>
                              <ListItemText primary={<Typography variant="body2" color="error">{err}</Typography>} />
                            </ListItem>
                            {i < importResult.errors.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
            <Button variant="contained" onClick={() => setImportResultOpen(false)} sx={{ borderRadius: 2 }}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        {/* --- ADD/EDIT DIALOG --- */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '80vh' } }}>
          <DialogTitle sx={{ borderBottom: '1px solid #f0f0f0', py: 2, px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">{editingProduct ? t('store.editProduct') : t('store.addProduct')}</Typography>
                {selectedCategoryForNew && targetCategory && <Typography variant="caption" color="text.secondary">{t('store.addingTo')}: {targetCategory.name}</Typography>}
              </Box>
              <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
              <Tab label={t('store.tabDetails')} sx={{ fontWeight: 600 }} />
              <Tab label={t('store.tabTranslations')} sx={{ fontWeight: 600 }} />
            </Tabs>
          </Box>
          <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>

            {/* TAB 1: DETAILS */}
            <TabPanel value={currentTab} index={0}>
              <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: 'text.secondary', letterSpacing: 1 }}>{t('store.basicInfo')}</Typography>
                        <Stack spacing={2}>
                          <TextField label={t('store.productName')} fullWidth required value={productName} onChange={(e) => setProductName(e.target.value)} size="small" />
                          <TextField label={t('services.description')} fullWidth multiline rows={3} value={productDescription} onChange={(e) => setProductDescription(e.target.value)} size="small" />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={productFeatured}
                                onChange={(e) => setProductFeatured(e.target.checked)}
                                color="primary"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StarIcon color={productFeatured ? "primary" : "disabled"} fontSize="small" />
                                <Typography variant="body2" color={productFeatured ? "primary" : "text.secondary"}>
                                  {t('store.featured')}
                                </Typography>
                              </Box>
                            }
                          />
                        </Stack>
                      </Paper>
                      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.secondary', letterSpacing: 1 }}>{t('store.imagesTitle')}</Typography>
                          <Typography variant="caption" color="text.secondary">{t('store.imageCount', { count: productImageUrls.length })}</Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={4} sm={3} md={2}>
                            <input accept="image/*" style={{ display: 'none' }} id="upload-btn" multiple type="file" onChange={handleImageUpload} />
                            <label htmlFor="upload-btn">
                              <Box sx={{ width: '100%', aspectRatio: '1/1', border: '1px dashed #667eea', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: 'rgba(102,126,234,0.04)', '&:hover': { bgcolor: 'rgba(102,126,234,0.1)' } }}>
                                {uploading ? <CircularProgress size={24} /> : <CloudUploadIcon color="primary" />}
                                <Typography variant="caption" sx={{ mt: 1, color: 'primary.main', fontWeight: 600 }}>{t('services.addButton')}</Typography>
                              </Box>
                            </label>
                          </Grid>
                          {productImageUrls.map((url, i) => (
                              <Grid item xs={4} sm={3} md={2} key={i}>
                                <Box sx={{ width: '100%', aspectRatio: '1/1', position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}>
                                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <IconButton size="small" onClick={() => handleRemoveImage(i)} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.9)', padding: '2px', '&:hover': { bgcolor: '#fff', color: 'error.main' } }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Box>
                              </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 3, color: 'text.secondary', letterSpacing: 1 }}>{t('store.inventorySection')}</Typography>
                      <Stack spacing={3}>
                        <TextField label={t('services.price')} type="number" fullWidth required value={productPrice} onChange={(e) => setProductPrice(e.target.value)} size="small" InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }} />
                        <TextField label={t('store.pricePer100g')} type="number" fullWidth value={productPricePer100g} onChange={(e) => setProductPricePer100g(e.target.value)} size="small" InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }} inputProps={{ min: 0, step: 'any' }} />
                        <TextField label={t('store.stock')} type="number" fullWidth required value={productStockQuantity} onChange={(e) => setProductStockQuantity(e.target.value)} size="small" />
                        <TextField label={t('store.sku')} fullWidth value={productSku} onChange={(e) => setProductSku(e.target.value)} size="small" />
                        {!selectedCategoryForNew && (
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('store.category')}</InputLabel>
                              <Select value={productCategoryId} onChange={(e) => setProductCategoryId(e.target.value)} label={t('store.category')}>
                                <MenuItem value=""><em>{t('store.noCategory')}</em></MenuItem>
                                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                              </Select>
                            </FormControl>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            {/* TAB 2: TRANSLATIONS (REQUESTED LOGIC) */}
            <TabPanel value={currentTab} index={1}>
              <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                <Grid container spacing={4}>
                  {/* Form Side */}
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#fff' }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>{t('store.addTranslationTitle')}</Typography>
                      <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('store.language')}</InputLabel>
                          <Select
                              value={selectedLocale}
                              onChange={(e) => setSelectedLocale(e.target.value)}
                              label={t('store.language')}
                          >
                            {supportedLocales
                                .filter(l => !productTranslations[l.code]) // Hide already added langs
                                .map(l => (
                                    <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
                                ))}
                          </Select>
                        </FormControl>
                        <TextField
                            label={t('store.translatedName')}
                            size="small"
                            fullWidth
                            required
                            value={translationName}
                            onChange={(e) => setTranslationName(e.target.value)}
                            inputProps={{ maxLength: 200 }}
                            helperText={t('store.charCount', { count: translationName.length, max: 200 })}
                        />
                        <TextField
                            label={t('store.translatedDescription')}
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            value={translationDescription}
                            onChange={(e) => setTranslationDescription(e.target.value)}
                            inputProps={{ maxLength: 2000 }}
                            helperText={t('store.charCount', { count: translationDescription.length, max: 2000 })}
                        />
                        <Button
                            variant="contained"
                            disabled={!selectedLocale || !translationName.trim()}
                            onClick={handleAddTranslation}
                            startIcon={<TranslateIcon />}
                        >
                          {t('store.addTranslationButton')}
                        </Button>
                        {Object.keys(productTranslations).length === supportedLocales.length && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            {t('store.allLanguagesAdded')}
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* List Side */}
                  <Grid item xs={12} md={7}>
                    <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                      <Box sx={{ p: 2, bgcolor: '#f5f5f7', borderBottom: '1px solid #eee' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {t('store.addedTranslations', { count: Object.keys(productTranslations).length })}
                        </Typography>
                      </Box>
                      <List disablePadding>
                        {Object.keys(productTranslations).length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                              <TranslateIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                              <Typography variant="body2">{t('store.noTranslations')}</Typography>
                            </Box>
                        ) : (
                            Object.entries(productTranslations).map(([code, data]) => {
                              const langName = supportedLocales.find(l => l.code === code)?.name || code.toUpperCase();
                              return (
                                  <React.Fragment key={code}>
                                    <ListItem alignItems="flex-start">
                                      <ListItemText
                                          primary={
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Chip label={langName} size="small" color="primary" variant="outlined" />
                                              <Typography component="span" fontWeight="500">{data.name}</Typography>
                                            </Box>
                                          }
                                          secondary={
                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                              {data.description || <em>{t('services.noDescription')}</em>}
                                            </Typography>
                                          }
                                      />
                                      <ListItemSecondaryAction>
                                        <IconButton edge="end" color="error" onClick={() => handleRemoveTranslation(code)}>
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                    <Divider component="li" />
                                  </React.Fragment>
                              );
                            })
                        )}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
            <Button onClick={handleCloseDialog} color="inherit">{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleSaveProduct} disabled={saving} sx={{ borderRadius: 2, px: 4 }}>{saving ? t('store.saving') : t('store.saveProduct')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

// --- MAIN CONTAINER ---
const StoreManagement = ({ providerData }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1); // Start on Products tab (index 0 = Delivery, index 1 = Products)

  return (
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              indicatorColor="primary"
              textColor="primary"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<DeliveryIcon />} iconPosition="start" label={t('delivery.tabTitle')} />
            <Tab icon={<StoreIcon />} iconPosition="start" label={t('nav.products')} />
          </Tabs>
        </Paper>

        {/* Delivery: price/time/minimum-order settings, then the delivery-zone polygon editor */}
        <TabPanel value={activeTab} index={0}>
          <DeliverySettings providerData={providerData} />
          <DeliveryZoneManagement providerData={providerData} />
        </TabPanel>

        {/* Products (With Translation Logic) */}
        <TabPanel value={activeTab} index={1}>
          <ProductsTabContent providerData={providerData} />
        </TabPanel>
      </Box>
  );
};

export default StoreManagement;